import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { transferHistoryTableColumns } from './columns';
import moment from 'moment';
import { Table, type TableState } from '../ui/Table';
import useAPI from '../../hooks/useAPI';
import styles from './style.module.scss';

export default function TransferHistory({ activeTab }: { activeTab: string }) {
  const [hasMore, setHasMore] = useState(true);
  const [data, setData] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [tableState, setTableState] = useState<TableState>({
    sort: { column: null, direction: null },
    filters: {},
  });

  const handleStateChange = (newState: TableState) => {
    setTableState(newState);
  };

  const {
    isLoading,
    data: transferHistory,
    refetch,
  } = useAPI({
    url: `/token/transfer/history`,
    method: 'GET',
    autoFetch: false,
    onSuccess: data => {
      const transferHistory = data.result.Tokens;
      setData((prevData: any[]) => {
        const mergedData = [...prevData, ...transferHistory];
        const uniqueData = mergedData.reduce((acc: any[], item: any) => {
          const findIndex = acc.findIndex(
            (i: any) => i.transactionId === item.transactionId,
          );
          if (findIndex !== -1) {
            acc[findIndex] = item;
          } else {
            acc.push(item);
          }
          return acc;
        }, []);
        const sortedData = uniqueData.sort((a: any, b: any) => {
          const dateA = moment(a.transactionDate);
          const dateB = moment(b.transactionDate);

          if (dateA.isBefore(dateB)) return 1;
          if (dateA.isAfter(dateB)) return -1;
          return 0;
        });
        return sortedData;
      });
    },
  });

  useEffect(() => {
    if (activeTab === 'transfer-history') {
      setPage(1);
      setData([]);
      refetch({ page: 1, limit: 10 });
    }
  }, [activeTab]);

  useEffect(() => {
    if (page > 1) {
      refetch({ page: page, limit: 10 });
    }
  }, [page]);

  useEffect(() => {
    if (transferHistory?.result?.totalPages !== undefined) {
      setHasMore(page < transferHistory.result.totalPages);
    }
  }, [page, transferHistory]);

  const loadMore = () => {
    if (!isLoading && hasMore && transferHistory?.result?.totalPages) {
      setPage(prevPage => prevPage + 1);
    }
  };

  return (
    <div className={styles.transferHistoryContainer}>
      <div ref={tableContainerRef} className={styles.tableScrollContainer}>
        <Table
          columns={transferHistoryTableColumns}
          data={data}
          loading={isLoading}
          hasMore={hasMore}
          onLoadMore={loadMore}
          onStateChange={handleStateChange}
          initialState={tableState}
          scrollableContainerRef={tableContainerRef}
        />
      </div>
    </div>
  );
}
